import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button, Spinner } from "reactstrap";

import api from "../services/api";
import { useToast } from "../hooks/toast";

// Exportação Excel em segundo plano das pesquisas (PO1-2969, épico PO1-2280).
// Usado pela Pesquisa CT-e e pela Pesquisa Importação: POST com os filtros
// aplicados → acompanha GET /pesquisa/exportacoes/:jobId a cada 3 s (até 200
// vezes, padrão GoBID/Sourcing) → toast de pronto e link com a URL pré-assinada
// (não passa pelo downloadBlob). 409 reaproveita o job ativo; 404 no meio do
// acompanhamento = job perdido em restart, tratado como falha para refazer.

export const POLL_MS = 3000;
export const POLL_MAX = 200;

const ESTADO_INICIAL = { fase: "idle", jobId: null, total: null, processadas: 0, url: null, nomeArquivo: null, erro: "" };

export function textoProgresso(estado) {
  if (estado.fase !== "gerando") return "";
  if (estado.total === null || estado.total === undefined) return "Gerando…";
  return `Gerando… ${estado.processadas || 0} de ${estado.total}`;
}

export default function ExportarPesquisaButton({ tipo, filtros, disabled = false, label = "Exportar Excel" }) {
  const { addToast } = useToast();
  const [estado, setEstado] = useState(ESTADO_INICIAL);
  const timerRef = useRef(null);
  const montadoRef = useRef(true);

  useEffect(() => {
    montadoRef.current = true;
    return () => {
      montadoRef.current = false;
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  const falhar = useCallback((mensagem) => {
    if (!montadoRef.current) return;
    setEstado((atual) => ({ ...atual, fase: "falhou", erro: mensagem }));
  }, []);

  const acompanhar = useCallback(
    (jobId) => {
      let tentativas = 0;
      const poll = async () => {
        if (!montadoRef.current) return;
        tentativas += 1;
        try {
          const res = await api.get(`/pesquisa/exportacoes/${jobId}`);
          const job = res.data || {};
          if (!montadoRef.current) return;
          if (job.status === "completed") {
            setEstado({ fase: "pronto", jobId, total: job.total, processadas: job.processadas, url: job.url, nomeArquivo: job.nomeArquivo, erro: "" });
            addToast({
              place: "tr",
              type: "success",
              title: "Exportação pronta",
              description: job.nomeArquivo ? `Clique em "Baixar ${job.nomeArquivo}".` : "Clique em Baixar para salvar o arquivo.",
              icon: "tim-icons icon-cloud-download-93",
            });
            return;
          }
          if (job.status === "failed") {
            falhar(job.erro || "A exportação falhou. Tente novamente.");
            return;
          }
          setEstado((atual) => ({ ...atual, fase: "gerando", jobId, total: job.total, processadas: job.processadas || 0 }));
        } catch (err) {
          if (err.response && err.response.status === 404) {
            falhar("A exportação se perdeu (o servidor foi reiniciado). Tente novamente.");
            return;
          }
          // erro transitório: tenta de novo até o limite
        }
        if (tentativas < POLL_MAX) {
          timerRef.current = setTimeout(poll, POLL_MS);
        } else {
          falhar("A exportação está demorando mais que o esperado. Tente novamente em instantes.");
        }
      };
      timerRef.current = setTimeout(poll, POLL_MS);
    },
    [addToast, falhar]
  );

  const iniciar = useCallback(async () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    setEstado({ ...ESTADO_INICIAL, fase: "gerando" });
    let jobId = null;
    try {
      const res = await api.post("/pesquisa/exportacoes", { tipo, filtros: filtros || {} });
      jobId = res.data && res.data.jobId;
    } catch (err) {
      const data = err.response && err.response.data;
      if (err.response && err.response.status === 409 && data && data.jobId) {
        jobId = data.jobId;
        addToast({
          place: "tr",
          type: "info",
          title: "Exportação em andamento",
          description: "Já existe uma exportação sua rodando; acompanhando essa.",
          icon: "tim-icons icon-time-alarm",
        });
      } else {
        const erro = data && data.error;
        const mensagem =
          typeof erro === "string"
            ? erro
            : erro && typeof erro === "object"
              ? Object.values(erro).join(" ")
              : "Não foi possível iniciar a exportação.";
        falhar(mensagem);
        return;
      }
    }
    if (!jobId) {
      falhar("Não foi possível iniciar a exportação.");
      return;
    }
    setEstado((atual) => ({ ...atual, jobId }));
    acompanhar(jobId);
  }, [acompanhar, addToast, falhar, filtros, tipo]);

  const gerando = estado.fase === "gerando";

  return (
    <span className="exportar-pesquisa" style={{ display: "inline-flex", alignItems: "center", gap: "0.5rem", flexWrap: "wrap" }}>
      {estado.fase === "pronto" && estado.url ? (
        <a
          className="btn btn-sm btn-info"
          href={estado.url}
          download={estado.nomeArquivo || undefined}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="exportar-pesquisa-link"
        >
          <i className="tim-icons icon-cloud-download-93" /> Baixar {estado.nomeArquivo || "arquivo"}
        </a>
      ) : null}
      <Button
        color="success"
        className="btn-sm"
        onClick={iniciar}
        disabled={disabled || gerando}
        title="Gera o Excel com os filtros aplicados; o arquivo fica pronto em segundo plano"
        data-testid="exportar-pesquisa-botao"
      >
        {gerando ? (
          <>
            <Spinner size="sm" /> <span data-testid="exportar-pesquisa-progresso">{textoProgresso(estado)}</span>
          </>
        ) : estado.fase === "pronto" ? (
          "Exportar de novo"
        ) : (
          label
        )}
      </Button>
      {estado.fase === "falhou" && estado.erro ? (
        <small style={{ color: "var(--everlog-status-danger)" }} data-testid="exportar-pesquisa-erro">
          {estado.erro}
        </small>
      ) : null}
    </span>
  );
}
